<template>
	<main>
		<ResponsiveLayout>
			<TopBottomLayout>
				<div class="ly_col_1">
					<div class="header">
						<div class="header_content">
							<div
								class="header_content_img"
								:style="{
									backgroundColor: String(etfQuery) ?? '',
									color: getTextColorForBackground(String(etfQuery) ?? ''),
								}"
							>
								<span>{{ etfEventDetailData?.etf_event_detail[0].etf_event_ticker }}</span>
							</div>
							<div class="header_content_txt">
								<h4>{{ etfEventDetailData?.etf_event_detail[0].etf_event_name }}</h4>
								<div class="header_content_txt_category">
									<span>{{ etfEventDetailData?.etf_event_detail[0].etf_event_ticker }}</span>
								</div>
							</div>
						</div>
						<p class="header_description">{{ etfEventDetailData?.etf_event_detail[0].etf_event_line_description }}</p>
					</div>
				</div>
				<div class="ly_col_1">
					<ExpandableTitleBar title="기업 개요" :has-view="false" />
					<div class="overview">
						<div v-for="(item, index) in etfBusinessOverviewItems" :key="index" class="overview_card">
							<div class="overview_card_item">
								<span class="category">{{ item }}</span
								><span v-if="item === '상장일'" class="content">{{
									etfEventDetailData?.etf_event_detail[0].etf_event_listing_date
								}}</span>
								<span v-if="item === '운용사'" class="content">{{
									etfEventDetailData?.etf_event_detail[0].etf_event_management_company
								}}</span>
								<span v-if="item === '자산규모'" class="content"
									>{{ etfEventDetailData?.etf_event_detail[0].etf_event_asset_size
									}}{{ etfEventDetailData?.etf_event_detail[0].etf_event_asset_unit }}</span
								>
								<span v-if="item === '총보수'" class="content">{{
									`${etfEventDetailData?.etf_event_detail[0].etf_event_total_compensation}%`
								}}</span>
							</div>
						</div>
					</div>
				</div>
				<div class="ly_col_1">
					<ExpandableTitleBar title="설명" :has-view="false" />
					<EditorBox
						><div v-dompurify-html="etfEventDetailData?.etf_event_detail[0].etf_event_description"></div
					></EditorBox>
				</div>
				<div class="ly_col-24">
					<ExpandableTitleBar title="보유 비중 Top10" :has-view="false" />
					<DoughnutChartItem
						v-if="isFetched"
						:data="chartOutlineData"
						:date="etfEventDetailData?.etf_event_detail[0].etf_event_base_date"
					/>
				</div>
			</TopBottomLayout>
		</ResponsiveLayout>
	</main>
</template>
<script setup lang="ts">
// core
import { computed, ref } from 'vue';
import { watch } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useGeEtfEventDetailApi } from '@/api/etf';
import DoughnutChartItem from '@/components/atom/chart/DoughnutChartItem.vue';
import ExpandableTitleBar from '@/components/atom/tabBar/ExpandableTitleBar.vue';
import EditorBox from '@/components/layout/common/EditorBox.vue';
import ResponsiveLayout from '@/components/layout/main/ResponsiveLayout.vue';
// component
import TopBottomLayout from '@/components/layout/main/TopBottomLayout.vue';
// constant
import { etfBusinessOverviewItems } from '@/constants/menu.ts';
// util
import { getTextColorForBackground } from '@/utils/function/convertValue';

// -----variable-----

const route = useRoute();
const etfQuery = route.query.color;

const isLoading = ref(true);
const etfPk = computed(() => String(route.params.etfPk) ?? '');
const etfEventPk = computed(() => String(route.params.etfEventPk) ?? '');

// -----api-----
const { data: etfEventDetailData, isFetched } = useGeEtfEventDetailApi(
	computed(() => ({ etf_pk: etfPk.value, etf_event_pk: etfEventPk.value }))
);
// -----chart-----
const chartOutlineData = computed(() => ({
	labels: etfEventDetailData.value?.etf_event_holding_proportion.map((item) => String(item.etf_holding_name)) ?? [],
	datasets: [
		{
			data:
				etfEventDetailData.value?.etf_event_holding_proportion.map((item) => Number(item.etf_holding_proportion)) ?? [],
			backgroundColor: [
				'#01A9FF',
				'#FFB83F',
				'#FF5946',
				'#DBAEFF',
				'#A9F4D0',
				'#2B8B5D',
				'#F49698',
				'#FEFFD5',
				'#CFD096',
				'#EAEAEA',
				'#3E61FF',
			],
			borderColor: 'transparent',
		},
	],
}));

// -----composition-----
watch(etfEventDetailData, (newData) => {
	if (newData) {
		isLoading.value = false;
	}
});
</script>

<style lang="scss" scoped>
.header {
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	&_content {
		display: flex;
		align-items: flex-start;
		gap: 0.8rem;
		&_img {
			display: flex;
			justify-content: center;
			align-items: center;
			max-width: 5.6rem;
			width: 100%;
			height: 5.6rem;
			border-radius: var(--border-radius-full);
			text-align: center;
			font-size: 1.2rem;
			font-weight: var(--font-w-semi-bold);
			line-height: 1.3rem;
			span {
				width: 3.2rem;
				overflow-wrap: break-word;
			}
		}
		&_txt {
			display: flex;
			flex-direction: column;
			gap: 0.8rem;
			font-size: var(--font-s-x-large);
			font-weight: var(--font-w-bold);
			line-height: 2.4rem;
			&_category {
				display: flex;
				align-items: center;
				gap: 0.8rem;
				color: var(--color-base-light);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: normal;
			}
		}
	}
	&_description {
		color: var(--color-base-light);
		font-size: var(--font-s-mid);
		font-weight: var(--font-w-light);
		line-height: 1.9rem;
	}
}

.overview {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1.2rem;
	background-color: var(--color-bg-mid);
	padding: 1.6rem;
	border-radius: 0.8rem;
	&_card {
		display: grid;
		gap: 1rem;

		&_item {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 0.4rem;
			.category {
				color: var(--color-base-light);
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-light);
				line-height: 1.9rem;
			}
			.content {
				color: var(--color-base-white);
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-semi-bold);
				line-height: 1.8rem;
			}
		}
	}
}
</style>
